<template>
  <div class="first-book-semantics">
    <el-alert
      title="字段语义兼容已启用：观察周期、长期关注级别、研究上下文与模拟状态均为新展示口径；旧字段原值继续保留，本阶段不自动迁移数据。"
      type="info"
      :closable="false"
      show-icon
      class="page-alert"
    />

    <el-row :gutter="16" class="summary-row">
      <el-col :xs="24" :sm="8">
        <el-card shadow="never" class="summary-card">
          <div class="summary-value">{{ totalCount }} / {{ stockTotalLimit }}</div>
          <div class="summary-label">全部股票</div>
          <div class="summary-hint">剩余容量 {{ capacityRemaining }} 只</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8">
        <el-card shadow="never" class="summary-card">
          <div class="summary-value formal">{{ formalCount }}</div>
          <div class="summary-label">正式区</div>
          <div class="summary-hint">无固定配额</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="8">
        <el-card shadow="never" class="summary-card">
          <div class="summary-value temp">{{ tempCount }}</div>
          <div class="summary-label">临时区</div>
          <div class="summary-hint">无固定配额</div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="toolbar-card">
      <div class="toolbar">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openAddDialog">
          新增到{{ activeTabLabel }}
        </el-button>
        <el-button size="small" icon="el-icon-document-add" @click="openBatchDialog">批量新增</el-button>
        <el-button size="small" icon="el-icon-remove-outline" @click="previewSelectedRemoval">批量移出</el-button>
        <el-button size="small" icon="el-icon-document" @click="openMigrationPreview">迁移预览</el-button>
        <el-input
          v-model.trim="searchKeyword"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="搜索代码、名称或关注理由"
          class="search-input"
        />
        <el-select v-model="observationFilter" size="small" clearable placeholder="观察周期" class="filter-select">
          <el-option v-for="item in observationOptions" :key="item" :label="item" :value="item" />
        </el-select>
        <el-select v-model="simulationFilter" size="small" clearable placeholder="模拟状态" class="filter-select">
          <el-option label="未参与模拟" value="not_involved" />
          <el-option label="模拟持仓中" value="open" />
          <el-option label="模拟仓待复核" value="review_due" />
          <el-option label="已平仓" value="closed" />
          <el-option label="已放弃" value="abandoned" />
          <el-option label="有历史记录" value="history_only" />
        </el-select>
        <span class="toolbar-spacer" />
        <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="refreshData">刷新</el-button>
      </div>
    </el-card>

    <el-card shadow="never">
      <el-tabs v-model="activeTab" @tab-click="clearSelections">
        <el-tab-pane name="formal">
          <span slot="label">正式区 <el-badge :value="formalCount" type="success" /></span>
          <el-table
            ref="formalTable"
            :data="filteredFormalList"
            stripe
            border
            size="small"
            v-loading="loading"
            @selection-change="selectedFormalRows = $event"
          >
            <el-table-column type="selection" width="42" />
            <el-table-column prop="ts_code" label="代码" width="110" fixed />
            <el-table-column prop="name" label="名称" width="105" fixed />
            <el-table-column label="观察周期" width="125">
              <template slot-scope="{ row }">
                <el-select
                  v-model="row.observation_value"
                  size="mini"
                  :disabled="isRowSaving(row)"
                  @change="updateObservation(row, $event)"
                >
                  <el-option v-for="item in observationOptions" :key="item" :label="item" :value="item" />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="长期关注级别" width="135">
              <template slot-scope="{ row }">
                <div class="inline-field">
                  <el-select
                    v-model="row.attention_value"
                    size="mini"
                    :disabled="isRowSaving(row)"
                    @change="updateAttention(row, $event)"
                  >
                    <el-option v-for="item in attentionOptions" :key="item" :label="item" :value="item" />
                  </el-select>
                  <el-tooltip v-if="row.legacy_today_priority" content="旧值为“今日优先”，已从长期标签退休，需在每日状态链中复核。" placement="top">
                    <i class="el-icon-warning legacy-warning" />
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="研究上下文" min-width="280">
              <template slot-scope="{ row }">
                <div class="context-cell">
                  <div class="context-main">{{ row.focus_reason || '待补充关注理由' }}</div>
                  <div class="context-sub">失效：{{ row.invalidation_condition || '待用户补充' }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="模拟状态" width="135">
              <template slot-scope="{ row }">
                <el-tag :type="simulationTagType(row.simulation_status)" size="mini">
                  {{ row.simulation_status_label }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="保存状态" width="90">
              <template slot-scope="{ row }">
                <span v-if="isRowSaving(row)" class="saving-text"><i class="el-icon-loading" /> 保存中</span>
                <span v-else class="saved-text">已同步</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="230" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
                <el-button type="text" size="small" @click="openNoteDialog(row)">编辑原始备注</el-button>
                <el-button type="text" size="small" @click="previewDowngrade(row)">降临时区</el-button>
                <el-dropdown trigger="click" @command="handleMoreCommand($event, row)">
                  <el-button type="text" size="small" class="more-button">更多<i class="el-icon-arrow-down" /></el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="remove" class="danger-command">移出第一本账</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane name="temp">
          <span slot="label">临时区 <el-badge :value="tempCount" type="primary" /></span>
          <el-table
            ref="tempTable"
            :data="filteredTempList"
            stripe
            border
            size="small"
            v-loading="loading"
            @selection-change="selectedTempRows = $event"
          >
            <el-table-column type="selection" width="42" />
            <el-table-column prop="ts_code" label="代码" width="110" fixed />
            <el-table-column prop="name" label="名称" width="105" fixed />
            <el-table-column label="候选状态" width="120">
              <template slot-scope="{ row }">
                <el-select
                  v-model="row.temp_status"
                  size="mini"
                  :disabled="isRowSaving(row)"
                  @change="updateRow(row, { temp_status: $event })"
                >
                  <el-option label="强候补" value="强候补" />
                  <el-option label="暂定" value="暂定" />
                  <el-option label="待删除" value="待删除" />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="研究上下文" min-width="300">
              <template slot-scope="{ row }">
                <div class="context-cell">
                  <div class="context-main">{{ row.focus_reason || '待补充关注理由' }}</div>
                  <div class="context-sub">失效：{{ row.invalidation_condition || '待用户补充' }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="模拟状态" width="135">
              <template slot-scope="{ row }">
                <el-tag :type="simulationTagType(row.simulation_status)" size="mini">
                  {{ row.simulation_status_label }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="保存状态" width="90">
              <template slot-scope="{ row }">
                <span v-if="isRowSaving(row)" class="saving-text"><i class="el-icon-loading" /> 保存中</span>
                <span v-else class="saved-text">已同步</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="235" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
                <el-button type="text" size="small" @click="openNoteDialog(row)">编辑原始备注</el-button>
                <el-button type="success" size="mini" @click="previewUpgrade(row)">升正式区</el-button>
                <el-dropdown trigger="click" @command="handleMoreCommand($event, row)">
                  <el-button type="text" size="small" class="more-button">更多<i class="el-icon-arrow-down" /></el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="remove" class="danger-command">移出第一本账</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <el-dialog :title="`新增到${dialogZoneLabel}`" :visible.sync="addDialogVisible" width="560px" :close-on-click-modal="false">
      <el-form :model="addForm" label-width="110px">
        <el-form-item label="股票代码/名称" required>
          <el-autocomplete
            v-model="addForm.keyword"
            :fetch-suggestions="fetchStockSuggestions"
            placeholder="输入代码或名称并从列表选择"
            value-key="value"
            style="width: 100%"
            @select="selectStockSuggestion"
          >
            <template slot-scope="{ item }">
              <span class="suggestion-code">{{ item.ts_code }}</span>
              <span class="suggestion-name">{{ item.name }}</span>
            </template>
          </el-autocomplete>
        </el-form-item>
        <el-form-item label="目标区域">
          <el-radio-group v-model="addForm.zone">
            <el-radio label="formal">正式区</el-radio>
            <el-radio label="temp">临时区</el-radio>
          </el-radio-group>
        </el-form-item>
        <template v-if="addForm.zone === 'formal'">
          <el-form-item label="观察周期">
            <el-select v-model="addForm.observation_horizon" style="width: 100%">
              <el-option v-for="item in observationOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
          <el-form-item label="长期关注级别">
            <el-select v-model="addForm.attention_level" style="width: 100%">
              <el-option v-for="item in attentionOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
        </template>
        <el-form-item v-else label="候选状态">
          <el-select v-model="addForm.temp_status" style="width: 100%">
            <el-option label="强候补" value="强候补" />
            <el-option label="暂定" value="暂定" />
            <el-option label="待删除" value="待删除" />
          </el-select>
        </el-form-item>
        <el-form-item label="原始备注">
          <el-input v-model="addForm.note" type="textarea" :rows="3" placeholder="可留空；不会再自动生成“波段/待分析”等伪信息" />
        </el-form-item>
        <el-form-item label="模拟参与权限">
          <el-tag type="info">仅建议、确认后模拟</el-tag>
          <div class="form-hint">本期不启用自动模拟，新增股票不会自动建立模拟仓。</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="previewSingleAdd">生成变更预览</el-button>
      </span>
    </el-dialog>

    <el-dialog title="批量新增股票" :visible.sync="batchDialogVisible" width="760px" :close-on-click-modal="false">
      <el-alert
        title="每行输入一个股票代码或名称。批量新增正式区时默认观察周期未设定、长期关注级别待定，不制造伪标签。"
        type="info"
        :closable="false"
        show-icon
        class="dialog-alert"
      />
      <el-radio-group v-model="batchZone" size="small" :disabled="Boolean(batchResolved)">
        <el-radio-button label="formal">正式区</el-radio-button>
        <el-radio-button label="temp">临时区</el-radio-button>
      </el-radio-group>
      <el-input
        v-if="!batchResolved"
        v-model="batchInput"
        type="textarea"
        :rows="9"
        class="batch-input"
        placeholder="688347\n贵州茅台\n000858\n比亚迪"
      />
      <div v-else class="resolved-panel">
        <div class="resolved-summary">
          已识别 {{ batchResolved.counts.resolved }} · 已存在 {{ batchResolved.counts.existing }} ·
          重复 {{ batchResolved.counts.duplicate }} · 无法识别 {{ batchResolved.counts.unresolved }}
        </div>
        <el-table :data="batchResolved.items" size="mini" border max-height="360">
          <el-table-column prop="input" label="原始输入" min-width="130" />
          <el-table-column prop="ts_code" label="代码" width="110" />
          <el-table-column prop="name" label="名称" width="110" />
          <el-table-column label="结果" width="100">
            <template slot-scope="{ row }">
              <el-tag :type="resolveStatusType(row.status)" size="mini">{{ resolveStatusLabel(row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="message" label="说明" min-width="150" />
        </el-table>
      </div>
      <span slot="footer">
        <el-button v-if="batchResolved" @click="resetBatchResolve">重新输入</el-button>
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button v-if="!batchResolved" type="primary" :loading="batchResolving" @click="resolveBatchInput">解析输入</el-button>
        <el-button
          v-else
          type="primary"
          :disabled="batchResolved.counts.resolved === 0"
          :loading="batchPreviewLoading"
          @click="previewResolvedBatchAdd"
        >生成统一预览</el-button>
      </span>
    </el-dialog>

    <el-dialog title="批量变更预览" :visible.sync="batchPreviewVisible" width="820px" :close-on-click-modal="false">
      <el-alert
        v-if="batchPreview && batchPreview.risk_warning"
        :title="batchPreview.risk_warning"
        type="warning"
        :closable="false"
        show-icon
        class="dialog-alert"
      />
      <div v-if="batchPreview" class="batch-capacity-line">
        当前 {{ batchCapacity.current_total }} 只；新增 {{ batchCapacity.creates }}；移出 {{ batchCapacity.deletes }}；
        确认后预计 {{ batchCapacity.projected_total }} / {{ batchCapacity.stock_total_limit }} 只
      </div>
      <el-table v-if="batchPreview" :data="batchPreview.results" size="mini" border max-height="420">
        <el-table-column prop="ts_code" label="代码" width="110" />
        <el-table-column prop="name" label="名称" width="110" />
        <el-table-column label="变更" width="140"><template slot-scope="{ row }">{{ operationLabel(row.op_type) }}</template></el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 'ready' ? 'success' : 'danger'" size="mini">{{ row.status === 'ready' ? '可确认' : '失败' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="reason" label="说明" min-width="220" show-overflow-tooltip />
      </el-table>
      <span slot="footer">
        <el-button @click="batchPreviewVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!batchPreview || batchPreview.ready === 0" :loading="batchConfirming" @click="confirmBatchPreview">
          确认执行 {{ batchPreview ? batchPreview.ready : 0 }} 项
        </el-button>
      </span>
    </el-dialog>

    <el-dialog title="中文变更预览" :visible.sync="diffDialogVisible" width="650px" :close-on-click-modal="false">
      <div v-if="pendingDiff" class="human-diff">
        <div class="diff-title">{{ operationLabel(pendingDiff.operation) }}</div>
        <div class="diff-stock">{{ pendingDiff.ts_code }} {{ pendingDiff.name }}</div>
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="变更前">{{ describeState(pendingDiff.before) }}</el-descriptions-item>
          <el-descriptions-item label="变更后">{{ describeState(pendingDiff.after) }}</el-descriptions-item>
          <el-descriptions-item label="安全边界">确认后写入并立即回读；不会触发模拟仓、任务或交易动作</el-descriptions-item>
        </el-descriptions>
        <el-alert v-if="pendingDiff.highRisk" title="这是成员移出或降区变更，请确认对象和目标状态。" type="warning" :closable="false" show-icon class="dialog-alert" />
      </div>
      <span slot="footer">
        <el-button @click="diffDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="diffConfirming" @click="confirmPendingDiff">确认写入</el-button>
      </span>
    </el-dialog>

    <el-dialog title="编辑原始备注" :visible.sync="noteDialogVisible" width="560px" :close-on-click-modal="false">
      <el-alert title="阶段 2 不自动迁移备注。这里修改的是兼容字段原文，研究上下文将在保存后重新解析。" type="info" :closable="false" show-icon class="dialog-alert" />
      <div class="note-stock">{{ noteForm.ts_code }} {{ noteForm.name }}</div>
      <el-input v-model="noteForm.note" type="textarea" :rows="5" />
      <span slot="footer">
        <el-button @click="noteDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="previewNoteUpdate">生成变更预览</el-button>
      </span>
    </el-dialog>

    <el-dialog title="字段语义与模拟联动详情" :visible.sync="detailVisible" width="760px">
      <div v-if="detailRow">
        <div class="detail-title">{{ detailRow.ts_code }} {{ detailRow.name }}</div>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="区域">{{ detailRow.zone === 'formal' ? '正式区' : '临时区' }}</el-descriptions-item>
          <el-descriptions-item label="模拟参与权限">仅建议、确认后模拟</el-descriptions-item>
          <el-descriptions-item label="观察周期">{{ detailRow.observation_value }}</el-descriptions-item>
          <el-descriptions-item label="旧静态标签">{{ detailRow.static_tag || '空' }}</el-descriptions-item>
          <el-descriptions-item label="长期关注级别">{{ detailRow.attention_value }}</el-descriptions-item>
          <el-descriptions-item label="旧动态标签">{{ detailRow.dynamic_tag || '空' }}</el-descriptions-item>
          <el-descriptions-item label="关注理由" :span="2">{{ detailRow.focus_reason || '待补充' }}</el-descriptions-item>
          <el-descriptions-item label="失效条件" :span="2">{{ detailRow.invalidation_condition || '待用户补充' }}</el-descriptions-item>
          <el-descriptions-item label="用户补充" :span="2">{{ detailRow.user_note || '无' }}</el-descriptions-item>
          <el-descriptions-item label="模拟状态">{{ detailRow.simulation_status_label }}</el-descriptions-item>
          <el-descriptions-item label="历史模拟次数">{{ detailRow.simulation_history_count }}</el-descriptions-item>
          <el-descriptions-item label="最近 sim_id" :span="2">{{ detailRow.latest_sim_id || '无' }}</el-descriptions-item>
        </el-descriptions>
        <div v-if="detailRow.market_fragments.length" class="detail-section">
          <div class="section-title">旧备注中的行情快照片段</div>
          <el-tag v-for="item in detailRow.market_fragments" :key="item" type="warning" size="mini" class="fragment-tag">{{ item }}</el-tag>
        </div>
        <div v-if="detailRow.quality_flags.length" class="detail-section">
          <div class="section-title">待复核项</div>
          <el-tag v-for="flag in detailRow.quality_flags" :key="flag" type="danger" size="mini" class="fragment-tag">{{ qualityFlagLabel(flag) }}</el-tag>
        </div>
        <div class="detail-section">
          <div class="section-title">旧备注原文</div>
          <div class="legacy-note">{{ detailRow.note || '空' }}</div>
        </div>
      </div>
    </el-dialog>

    <el-dialog title="旧字段迁移预览（只读）" :visible.sync="migrationVisible" width="900px" :close-on-click-modal="false">
      <el-alert
        v-if="migrationPreview"
        :title="`预览编号：${migrationPreview.preview_id}。本预览未修改数据库，未来正式应用仍需你确认。`"
        type="success"
        :closable="false"
        show-icon
        class="dialog-alert"
      />
      <el-table v-if="migrationPreview" :data="migrationPreview.items" size="mini" border max-height="500">
        <el-table-column prop="ts_code" label="代码" width="110" />
        <el-table-column prop="name" label="名称" width="105" />
        <el-table-column label="旧字段" min-width="220">
          <template slot-scope="{ row }">
            <div>静态：{{ row.before.static_tag || '空' }}</div>
            <div>动态：{{ row.before.dynamic_tag || '空' }}</div>
            <div>备注：{{ compactText(row.before.note, 55) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="新展示语义" min-width="260">
          <template slot-scope="{ row }">
            <div>观察周期：{{ row.after.observation_horizon }}</div>
            <div>关注级别：{{ row.after.attention_level }}</div>
            <div>关注理由：{{ row.after.research_context.focus_reason || '待补充' }}</div>
            <div>模拟状态：{{ row.after.simulation_link.status_label }}</div>
          </template>
        </el-table-column>
        <el-table-column label="待复核" min-width="190">
          <template slot-scope="{ row }">
            <el-tag v-for="flag in row.quality_flags" :key="flag" type="warning" size="mini" class="preview-flag">{{ qualityFlagLabel(flag) }}</el-tag>
            <span v-if="!row.quality_flags.length">无</span>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer"><el-button @click="migrationVisible = false">关闭</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import { firstBookP0Api, firstBookErrorMessage } from '@/api/firstBookP0'

export default {
  name: 'FirstBookSemantics',
  data() {
    return {
      activeTab: 'formal',
      loading: false,
      submitting: false,
      formalList: [],
      tempList: [],
      stockTotalLimit: 100,
      capacityContract: {},
      semanticsContract: {},
      searchKeyword: '',
      observationFilter: '',
      simulationFilter: '',
      selectedFormalRows: [],
      selectedTempRows: [],
      rowSaving: {},
      addDialogVisible: false,
      addForm: this.emptyAddForm('formal'),
      batchDialogVisible: false,
      batchZone: 'formal',
      batchInput: '',
      batchResolved: null,
      batchResolving: false,
      batchPreviewLoading: false,
      batchPreviewVisible: false,
      batchPreview: null,
      batchConfirming: false,
      diffDialogVisible: false,
      diffConfirming: false,
      pendingDiff: null,
      noteDialogVisible: false,
      noteForm: { ts_code: '', name: '', note: '' },
      detailVisible: false,
      detailRow: null,
      migrationVisible: false,
      migrationPreview: null
    }
  },
  computed: {
    observationOptions() {
      return (((this.semanticsContract || {}).observation_horizon || {}).display_values) || ['未设定', '短期', '波段', '中长期']
    },
    attentionOptions() {
      return (((this.semanticsContract || {}).attention_level || {}).display_values) || ['待定', '重点', '常规']
    },
    formalCount() { return this.formalList.length },
    tempCount() { return this.tempList.length },
    totalCount() { return this.formalCount + this.tempCount },
    capacityRemaining() { return Math.max(0, this.stockTotalLimit - this.totalCount) },
    activeTabLabel() { return this.activeTab === 'formal' ? '正式区' : '临时区' },
    dialogZoneLabel() { return this.addForm.zone === 'formal' ? '正式区' : '临时区' },
    filteredFormalList() { return this.filterRows(this.formalList) },
    filteredTempList() { return this.filterRows(this.tempList) },
    selectedRows() { return this.activeTab === 'formal' ? this.selectedFormalRows : this.selectedTempRows },
    batchCapacity() {
      return (this.batchPreview && this.batchPreview.capacity) || {
        current_total: this.totalCount,
        creates: 0,
        deletes: 0,
        projected_total: this.totalCount,
        stock_total_limit: this.stockTotalLimit
      }
    }
  },
  created() { this.refreshData() },
  methods: {
    emptyAddForm(zone) {
      return {
        keyword: '', ts_code: '', name: '', zone,
        observation_horizon: '未设定', attention_level: '待定',
        temp_status: '强候补', note: ''
      }
    },
    normalizeSemanticItem(item) {
      const legacy = item.legacy || {}
      const context = item.research_context || {}
      const simulation = item.simulation_link || {}
      const observation = item.observation_horizon || {}
      const attention = item.attention_level || {}
      return {
        ...item,
        static_tag: legacy.static_tag || null,
        dynamic_tag: legacy.dynamic_tag || null,
        temp_status: legacy.temp_status || null,
        note: legacy.note || '',
        observation_value: observation.value || '未设定',
        attention_value: attention.value || (item.zone === 'formal' ? '待定' : '不适用'),
        legacy_today_priority: Boolean(attention.legacy_today_priority),
        focus_reason: context.focus_reason || '',
        invalidation_condition: context.invalidation_condition || '待用户补充',
        user_note: context.user_note || '',
        market_fragments: context.market_snapshot_fragments || [],
        quality_flags: item.quality_flags || [],
        simulation_status: simulation.status || 'not_involved',
        simulation_status_label: simulation.status_label || '未参与模拟',
        simulation_history_count: Number(simulation.history_count || 0),
        latest_sim_id: simulation.latest_sim_id || null
      }
    },
    async refreshData() {
      this.loading = true
      try {
        const [semantics, capacity, contract] = await Promise.all([
          firstBookP0Api.getSemanticsCurrent(),
          firstBookP0Api.getContract(),
          firstBookP0Api.getSemanticsContract()
        ])
        this.formalList = (semantics.formal_data || []).map(this.normalizeSemanticItem)
        this.tempList = (semantics.temp_data || []).map(this.normalizeSemanticItem)
        this.capacityContract = capacity || {}
        this.semanticsContract = contract || {}
        this.stockTotalLimit = Number(capacity.stock_total_limit || 100)
      } catch (error) {
        this.$message.error(firstBookErrorMessage(error, '读取第一本账新语义失败'))
      } finally {
        this.loading = false
      }
    },
    filterRows(rows) {
      const keyword = this.searchKeyword.trim().toLowerCase()
      return rows.filter(row => {
        const keywordMatch = !keyword || [row.ts_code, row.name, row.focus_reason, row.note]
          .some(value => String(value || '').toLowerCase().includes(keyword))
        const observationMatch = !this.observationFilter || row.observation_value === this.observationFilter
        const simulationMatch = !this.simulationFilter || row.simulation_status === this.simulationFilter || (this.simulationFilter === 'history_only' && row.simulation_history_count > 0)
        return keywordMatch && observationMatch && simulationMatch
      })
    },
    clearSelections() {
      this.selectedFormalRows = []
      this.selectedTempRows = []
      this.$nextTick(() => {
        if (this.$refs.formalTable) this.$refs.formalTable.clearSelection()
        if (this.$refs.tempTable) this.$refs.tempTable.clearSelection()
      })
    },
    isRowSaving(row) { return Boolean(this.rowSaving[row.ts_code]) },
    setRowSaving(tsCode, value) { this.$set(this.rowSaving, tsCode, value) },
    legacyObservation(display) {
      const mapping = (((this.semanticsContract || {}).observation_horizon || {}).display_to_legacy) || { '未设定': null, '短期': '短线', '波段': '波段', '中长期': '趋势' }
      return Object.prototype.hasOwnProperty.call(mapping, display) ? mapping[display] : null
    },
    legacyAttention(display) {
      const mapping = (((this.semanticsContract || {}).attention_level || {}).display_to_legacy) || { '待定': null, '重点': '重点跟踪', '常规': '普通跟踪' }
      return Object.prototype.hasOwnProperty.call(mapping, display) ? mapping[display] : null
    },
    async updateObservation(row, display) {
      await this.updateRow(row, { static_tag: this.legacyObservation(display) })
    },
    async updateAttention(row, display) {
      await this.updateRow(row, { dynamic_tag: this.legacyAttention(display) })
    },
    async updateRow(row, changes) {
      this.setRowSaving(row.ts_code, true)
      try {
        const opType = Object.prototype.hasOwnProperty.call(changes, 'temp_status') ? 'update_status' : 'update_tag'
        const preview = await firstBookP0Api.generateDiff({ op_type: opType, ts_code: row.ts_code, name: row.name, changes })
        await firstBookP0Api.confirmChange(preview.diff_id)
        this.$message.success('已保存并按新语义回读')
        await this.refreshData()
      } catch (error) {
        this.$message.error(firstBookErrorMessage(error, '保存失败，已恢复原值'))
        await this.refreshData()
      } finally {
        this.setRowSaving(row.ts_code, false)
      }
    },
    openAddDialog() {
      this.addForm = this.emptyAddForm(this.activeTab)
      this.addDialogVisible = true
    },
    async fetchStockSuggestions(query, callback) {
      const keyword = String(query || '').trim()
      try {
        const response = keyword ? await firstBookP0Api.getSuggestions(keyword, 20) : await firstBookP0Api.getPopularStocks('')
        const list = response.data || response.items || []
        callback(list.slice(0, 20).map(this.mapSuggestion))
      } catch (error) { callback([]) }
    },
    mapSuggestion(item) {
      const tsCode = item.ts_code || item.code6 || ''
      const name = item.name || ''
      return { value: `${tsCode} ${name}`.trim(), ts_code: tsCode, name }
    },
    selectStockSuggestion(item) {
      this.addForm.ts_code = item.ts_code
      this.addForm.name = item.name
      this.addForm.keyword = `${item.ts_code} ${item.name}`.trim()
    },
    async resolveSingleSelection() {
      if (this.addForm.ts_code && this.addForm.name) return true
      const raw = this.addForm.keyword.trim()
      if (!raw) return false
      const resolved = await firstBookP0Api.resolveBatch([raw], this.addForm.zone)
      const item = (resolved.items || []).find(entry => entry.status === 'resolved')
      if (!item) {
        this.$message.warning('请从联想列表选择股票，或输入可准确识别的代码/名称')
        return false
      }
      this.addForm.ts_code = item.ts_code
      this.addForm.name = item.name
      return true
    },
    async previewSingleAdd() {
      this.submitting = true
      try {
        if (!(await this.resolveSingleSelection())) return
        const changes = { zone: this.addForm.zone }
        if (this.addForm.zone === 'formal') {
          changes.static_tag = this.legacyObservation(this.addForm.observation_horizon)
          changes.dynamic_tag = this.legacyAttention(this.addForm.attention_level)
          changes.note = this.addForm.note || null
        } else {
          changes.temp_status = this.addForm.temp_status
          changes.note = this.addForm.note || null
        }
        const operation = this.addForm.zone === 'formal' ? 'add_formal' : 'add_temp'
        const response = await firstBookP0Api.generateDiff({
          op_type: operation, ts_code: this.addForm.ts_code, name: this.addForm.name, changes
        })
        this.openDiffPreview(response, operation, this.addForm.ts_code, this.addForm.name, false)
        this.addDialogVisible = false
      } catch (error) {
        this.$message.error(firstBookErrorMessage(error, '生成新增预览失败'))
      } finally { this.submitting = false }
    },
    openBatchDialog() {
      this.batchZone = this.activeTab
      this.batchInput = ''
      this.batchResolved = null
      this.batchDialogVisible = true
    },
    resetBatchResolve() { this.batchResolved = null },
    async resolveBatchInput() {
      const entries = this.batchInput.split(/\r?\n/).map(item => item.trim()).filter(Boolean)
      if (!entries.length) { this.$message.warning('请输入至少一只股票'); return }
      this.batchResolving = true
      try { this.batchResolved = await firstBookP0Api.resolveBatch(entries, this.batchZone) }
      catch (error) { this.$message.error(firstBookErrorMessage(error, '批量解析失败')) }
      finally { this.batchResolving = false }
    },
    async previewResolvedBatchAdd() {
      const readyItems = (this.batchResolved.items || []).filter(item => item.status === 'resolved')
      const items = readyItems.map(item => this.batchZone === 'formal'
        ? { op_type: 'add_formal', ts_code: item.ts_code, name: item.name, changes: { zone: 'formal', static_tag: null, dynamic_tag: null, note: null } }
        : { op_type: 'add_temp', ts_code: item.ts_code, name: item.name, changes: { zone: 'temp', temp_status: '强候补', note: null } })
      await this.createBatchPreview(items, `页面批量新增到${this.batchZone === 'formal' ? '正式区' : '临时区'}`)
      if (this.batchPreview) this.batchDialogVisible = false
    },
    async previewSelectedRemoval() {
      if (!this.selectedRows.length) { this.$message.warning('请先勾选需要移出的股票'); return }
      const items = this.selectedRows.map(row => ({ op_type: 'delete', ts_code: row.ts_code, name: row.name, changes: {} }))
      await this.createBatchPreview(items, `页面批量移出${items.length}只股票`)
    },
    async createBatchPreview(items, reason) {
      this.batchPreviewLoading = true
      try {
        this.batchPreview = await firstBookP0Api.previewBatch(items, reason)
        this.batchPreviewVisible = true
      } catch (error) {
        this.batchPreview = null
        this.$message.error(firstBookErrorMessage(error, '生成批量预览失败'))
      } finally { this.batchPreviewLoading = false }
    },
    async confirmBatchPreview() {
      if (!this.batchPreview || !this.batchPreview.batch_diff_id) return
      this.batchConfirming = true
      try {
        const result = await firstBookP0Api.confirmBatch(this.batchPreview.batch_diff_id)
        if (!result.success) throw new Error('批量事务未全部成功')
        this.$message.success(`批量变更完成：成功 ${result.success_count || result.total || 0} 项`)
        this.batchPreviewVisible = false
        this.batchPreview = null
        this.clearSelections()
        await this.refreshData()
      } catch (error) {
        this.$message.error(firstBookErrorMessage(error, '批量确认失败，事务已回滚'))
      } finally { this.batchConfirming = false }
    },
    openNoteDialog(row) {
      this.noteForm = { ts_code: row.ts_code, name: row.name, note: row.note || '' }
      this.noteDialogVisible = true
    },
    async previewNoteUpdate() {
      this.submitting = true
      try {
        const response = await firstBookP0Api.generateDiff({
          op_type: 'update_tag', ts_code: this.noteForm.ts_code, name: this.noteForm.name,
          changes: { note: this.noteForm.note || null }
        })
        this.openDiffPreview(response, 'update_note', this.noteForm.ts_code, this.noteForm.name, false)
        this.noteDialogVisible = false
      } catch (error) { this.$message.error(firstBookErrorMessage(error, '生成备注预览失败')) }
      finally { this.submitting = false }
    },
    async previewUpgrade(row) {
      try {
        const response = await firstBookP0Api.generateDiff({
          op_type: 'upgrade_to_formal', ts_code: row.ts_code, name: row.name,
          changes: { zone: 'formal', static_tag: this.legacyObservation(row.observation_value), dynamic_tag: null, note: row.note || null }
        })
        this.openDiffPreview(response, 'upgrade_to_formal', row.ts_code, row.name, false)
      } catch (error) { this.$message.error(firstBookErrorMessage(error, '生成升区预览失败')) }
    },
    async previewDowngrade(row) {
      try {
        const response = await firstBookP0Api.generateDiff({
          op_type: 'downgrade_to_temp', ts_code: row.ts_code, name: row.name,
          changes: { zone: 'temp', temp_status: '暂定', note: row.note || null }
        })
        this.openDiffPreview(response, 'downgrade_to_temp', row.ts_code, row.name, true)
      } catch (error) { this.$message.error(firstBookErrorMessage(error, '生成降区预览失败')) }
    },
    handleMoreCommand(command, row) { if (command === 'remove') this.previewRemove(row) },
    async previewRemove(row) {
      try {
        const response = await firstBookP0Api.generateDiff({ op_type: 'delete', ts_code: row.ts_code, name: row.name, changes: {} })
        this.openDiffPreview(response, 'delete', row.ts_code, row.name, true)
      } catch (error) { this.$message.error(firstBookErrorMessage(error, '生成移出预览失败')) }
    },
    openDiffPreview(response, operation, tsCode, name, highRisk) {
      const preview = response.diff_preview || {}
      this.pendingDiff = { diff_id: response.diff_id, operation, ts_code: tsCode, name, before: preview.before || null, after: preview.after || null, highRisk }
      this.diffDialogVisible = true
    },
    async confirmPendingDiff() {
      if (!this.pendingDiff || !this.pendingDiff.diff_id) return
      this.diffConfirming = true
      try {
        await firstBookP0Api.confirmChange(this.pendingDiff.diff_id)
        this.$message.success('变更已写入并按新语义回读')
        this.diffDialogVisible = false
        this.pendingDiff = null
        await this.refreshData()
      } catch (error) { this.$message.error(firstBookErrorMessage(error, '确认写入失败')) }
      finally { this.diffConfirming = false }
    },
    openDetail(row) { this.detailRow = row; this.detailVisible = true },
    async openMigrationPreview() {
      try {
        this.migrationPreview = await firstBookP0Api.getMigrationPreview()
        this.migrationVisible = true
      } catch (error) { this.$message.error(firstBookErrorMessage(error, '读取迁移预览失败')) }
    },
    operationLabel(operation) {
      return {
        add_formal: '新增到正式区', add_temp: '新增到临时区', create: '新增股票',
        update_tag: '更新字段', update_status: '更新候选状态', update_note: '更新原始备注',
        upgrade_to_formal: '升入正式区', promote: '升入正式区',
        downgrade_to_temp: '降入临时区', demote: '降入临时区', delete: '移出第一本账'
      }[operation] || operation || '变更'
    },
    describeState(state) {
      if (!state) return '无'
      const parts = []
      if (state.zone) parts.push(`区域：${state.zone === 'formal' ? '正式区' : '临时区'}`)
      if (Object.prototype.hasOwnProperty.call(state, 'static_tag')) parts.push(`观察周期：${this.displayObservation(state.static_tag)}`)
      if (Object.prototype.hasOwnProperty.call(state, 'dynamic_tag')) parts.push(`长期关注级别：${this.displayAttention(state.dynamic_tag)}`)
      if (state.temp_status) parts.push(`候选状态：${state.temp_status}`)
      if (Object.prototype.hasOwnProperty.call(state, 'note')) parts.push(`原始备注：${state.note || '空'}`)
      return parts.length ? parts.join('；') : '无可展示字段'
    },
    displayObservation(legacy) { return ({ '短线': '短期', '波段': '波段', '趋势': '中长期' })[legacy] || '未设定' },
    displayAttention(legacy) { return legacy === '今日优先' ? '待复核' : (({ '重点跟踪': '重点', '普通跟踪': '常规' })[legacy] || '待定') },
    simulationTagType(status) {
      return ({ open: 'success', review_due: 'warning', closed: 'info', abandoned: 'danger', history_only: 'info', unavailable: 'warning' })[status] || ''
    },
    resolveStatusType(status) { return ({ resolved: 'success', existing: 'info', duplicate: 'warning', unresolved: 'danger' })[status] || 'info' },
    resolveStatusLabel(status) { return ({ resolved: '已识别', existing: '已存在', duplicate: '重复', unresolved: '无法识别' })[status] || status },
    compactText(value, limit) {
      const text = String(value || '空')
      return text.length > limit ? `${text.slice(0, limit)}…` : text
    },
    qualityFlagLabel(flag) {
      return ({
        legacy_note_contains_market_snapshot: '旧备注含行情快照',
        missing_invalidation_condition: '缺失失效条件',
        missing_focus_reason: '缺失关注理由',
        research_context_empty: '研究上下文为空',
        legacy_today_priority_requires_daily_status_review: '旧今日优先待复核',
        legacy_short_line_mapped_to_short_term: '短线映射为短期',
        observation_horizon_unset: '观察周期未设定',
        attention_level_pending: '关注级别待定',
        unknown_legacy_observation_horizon: '未知旧观察周期',
        unknown_legacy_attention_level: '未知旧关注级别',
        simulation_ledger_unavailable: '模拟账本暂不可用'
      })[flag] || flag
    }
  }
}
</script>

<style scoped>
.first-book-semantics { padding-bottom: 24px; }
.page-alert, .summary-row, .toolbar-card { margin-bottom: 16px; }
.summary-card { text-align: center; }
.summary-value { font-size: 30px; line-height: 1.2; font-weight: 700; color: #409eff; }
.summary-value.formal { color: #67c23a; }
.summary-value.temp { color: #e6a23c; }
.summary-label { margin-top: 6px; color: #303133; font-weight: 600; }
.summary-hint { margin-top: 4px; color: #909399; font-size: 12px; }
.toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.toolbar-spacer { flex: 1; }
.search-input { width: 245px; }
.filter-select { width: 130px; }
.inline-field { display: flex; align-items: center; gap: 5px; }
.legacy-warning { color: #e6a23c; cursor: help; }
.context-cell { line-height: 1.45; }
.context-main { color: #303133; }
.context-sub { color: #909399; font-size: 12px; margin-top: 2px; }
.saving-text { color: #e6a23c; }
.saved-text { color: #67c23a; }
.more-button { margin-left: 8px; }
.danger-command { color: #f56c6c; }
.suggestion-code { display: inline-block; width: 110px; color: #606266; }
.suggestion-name { color: #303133; }
.form-hint { margin-top: 5px; color: #909399; font-size: 12px; }
.dialog-alert { margin-bottom: 14px; }
.batch-input { margin-top: 14px; }
.resolved-panel { margin-top: 14px; }
.resolved-summary, .batch-capacity-line { margin-bottom: 10px; color: #606266; font-size: 13px; }
.diff-title { font-size: 18px; font-weight: 700; color: #303133; }
.diff-stock, .note-stock { margin: 6px 0 14px; color: #606266; }
.detail-title { margin-bottom: 14px; font-size: 18px; font-weight: 700; }
.detail-section { margin-top: 16px; }
.section-title { margin-bottom: 8px; color: #606266; font-weight: 600; }
.fragment-tag, .preview-flag { margin: 0 6px 6px 0; }
.legacy-note { padding: 10px; background: #f5f7fa; border-radius: 4px; color: #606266; line-height: 1.6; }
@media (max-width: 900px) {
  .search-input, .filter-select { width: 100%; }
  .toolbar-spacer { display: none; }
}
</style>
